import React, { useEffect, useState } from 'react';
import { validateDisplayName, validatePassword, validateEmail } from '@big2/shared';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';

function GoogleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      />
    </svg>
  );
}

export type AuthViewMode = 'signup' | 'signin' | 'verify' | 'forgot' | 'reset';

export function AuthForms({ initialView, onSuccess }: { initialView?: AuthViewMode; onSuccess?: () => void } = {}) {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';

  const [view, setView] = useState<AuthViewMode>(() => {
    if (state.recoveryToken || state.authNotice?.kind === 'password_reset_ready') return 'reset';
    if (state.verificationRequired || state.authNotice?.kind === 'verification_sent') return 'verify';
    return initialView || 'signup';
  });

  const [email, setEmail] = useState(() => state.pendingVerificationEmail || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [displayName, setDisplayName] = useState(
    () => (typeof localStorage !== 'undefined' ? localStorage.getItem('big2.name') || '' : ''),
  );

  useEffect(() => {
    if (state.pendingVerificationEmail && !email) {
      setEmail(state.pendingVerificationEmail);
    }
  }, [state.pendingVerificationEmail, email]);

  useEffect(() => {
    if (state.profile && onSuccess) {
      onSuccess();
    }
  }, [state.profile, onSuccess]);

  useEffect(() => {
    if (state.recoveryToken || state.authNotice?.kind === 'password_reset_ready') {
      setView('reset');
    } else if (state.authNotice?.kind === 'password_reset_sent') {
      setView('reset');
    } else if (state.verificationRequired || state.authNotice?.kind === 'verification_sent') {
      setView('verify');
    }
  }, [state.recoveryToken, state.verificationRequired, state.authNotice?.kind]);

  useEffect(() => {
    if (state.error) {
      setIsOAuthLoading(false);
    }
  }, [state.error]);

  useEffect(() => {
    if (!localError) return;
    const timer = setTimeout(() => {
      setLocalError(null);
    }, 20_000);
    return () => clearTimeout(timer);
  }, [localError]);

  const switchView = (next: AuthViewMode) => {
    setLocalError(null);
    actions.clearError();
    actions.clearAuthNotice();
    setView(next);
  };

  const handleGoogleSignIn = () => {
    setIsOAuthLoading(true);
    actions.loginWithOAuth('google');
  };

  const handleAuthSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (view === 'signup') {
      const nameRes = validateDisplayName(displayName.trim());
      if (!nameRes.valid) {
        setLocalError(isMn ? nameRes.errorMn! : nameRes.errorEn!);
        return;
      }
      const emailRes = validateEmail(email.trim());
      if (!emailRes.valid) {
        setLocalError(isMn ? emailRes.errorMn! : emailRes.errorEn!);
        return;
      }
      const passRes = validatePassword(password);
      if (!passRes.valid) {
        setLocalError(isMn ? passRes.errorMn! : passRes.errorEn!);
        return;
      }
      if (confirmPassword && password !== confirmPassword) {
        setLocalError(isMn ? 'Нууц үг хоорондоо таарахгүй байна.' : 'Passwords do not match.');
        return;
      }
      actions.register(email.trim(), password, displayName.trim());
    } else if (view === 'signin') {
      const emailRes = validateEmail(email.trim());
      if (!emailRes.valid) {
        setLocalError(isMn ? emailRes.errorMn! : emailRes.errorEn!);
        return;
      }
      if (!password) {
        setLocalError(isMn ? 'Нууц үгээ оруулна уу.' : 'Please enter your password.');
        return;
      }
      actions.login(email.trim(), password);
    }
  };

  const handleVerifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const targetEmail = (email || state.pendingVerificationEmail || '').trim();
    if (!targetEmail || !otpCode.trim()) return;
    actions.verifyEmail({
      email: targetEmail,
      token: otpCode.trim(),
      type: 'signup',
    });
  };

  const handleForgotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const emailRes = validateEmail(email.trim());
    if (!emailRes.valid) {
      setLocalError(isMn ? emailRes.errorMn! : emailRes.errorEn!);
      return;
    }
    actions.forgotPassword(email.trim());
  };

  const handleResetSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const passRes = validatePassword(password);
    if (!passRes.valid) {
      setLocalError(isMn ? passRes.errorMn! : passRes.errorEn!);
      return;
    }
    if (confirmPassword && password !== confirmPassword) {
      setLocalError(isMn ? 'Шинэ нууц үг хоорондоо таарахгүй байна.' : 'New passwords do not match.');
      return;
    }
    if (state.recoveryToken) {
      actions.resetPassword({
        recoveryToken: state.recoveryToken,
        newPassword: password,
      });
    } else {
      const targetEmail = (email || state.pendingVerificationEmail || '').trim();
      actions.resetPassword({
        email: targetEmail,
        token: otpCode.trim(),
        newPassword: password,
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          background: 'rgba(251, 191, 36, 0.14)',
          border: '1px solid rgba(251, 191, 36, 0.4)',
          borderRadius: 10,
          padding: '10px 14px',
          fontSize: '0.88em',
          color: '#fde68a',
          lineHeight: 1.4,
        }}
      >
        {t.newUserBonusBanner}
      </div>

      {/* Dev Quick Login Buttons (Local Development Only) */}
      {import.meta.env.DEV && (view === 'signup' || view === 'signin') && (
        <div
          style={{
            background: 'rgba(251, 191, 36, 0.08)',
            border: '1px dashed rgba(251, 191, 36, 0.45)',
            borderRadius: 10,
            padding: '10px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#fde68a', letterSpacing: '0.04em' }}>
              ⚡ DEV QUICK LOGIN (LOCAL TEST)
            </span>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.55)' }}>
              pw: password123
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { name: 'Alice', email: 'alice@test.com', label: '👑 VIP · 50k' },
              { name: 'Bob', email: 'bob@test.com', label: 'Free · 30k' },
              { name: 'Charlie', email: 'charlie@test.com', label: 'Free · 20k' },
            ].map((u) => (
              <button
                key={u.email}
                type="button"
                onClick={() => {
                  setEmail(u.email);
                  setPassword('password123');
                  actions.login(u.email, 'password123');
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  color: '#fff',
                  padding: '7px 4px',
                  borderRadius: 8,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  textAlign: 'center',
                  lineHeight: 1.25,
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(251, 191, 36, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                }}
              >
                <div style={{ fontWeight: 700, color: '#fde68a' }}>{u.name}</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.7)' }}>{u.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Google Sign-in / One-tap */}
      {(view === 'signup' || view === 'signin') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            className="google-auth-button"
            onClick={handleGoogleSignIn}
            disabled={isOAuthLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              background: '#ffffff',
              color: '#1f2937',
              border: 'none',
              borderRadius: 10,
              padding: '11px 16px',
              fontSize: '0.94rem',
              fontWeight: 700,
              cursor: isOAuthLoading ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
              opacity: isOAuthLoading ? 0.75 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            <GoogleIcon size={19} />
            <span>{isOAuthLoading ? (isMn ? 'Холбогдож байна...' : 'Connecting...') : t.continueWithGoogle}</span>
          </button>

          <div
            style={{
              textAlign: 'center',
              fontSize: '0.78rem',
              color: 'rgba(255, 255, 255, 0.65)',
              marginTop: -2,
            }}
          >
            {t.guestSaveNotice}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              color: 'rgba(255, 255, 255, 0.45)',
              fontSize: '0.78rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <div style={{ flex: 1, height: 1, background: 'rgba(255, 255, 255, 0.15)' }} />
            <span>{t.orWithEmail}</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255, 255, 255, 0.15)' }} />
          </div>
        </div>
      )}

      {/* Primary Tabs (Sign Up / Sign In) */}
      {(view === 'signup' || view === 'signin') && (
        <div
          style={{
            display: 'flex',
            background: 'rgba(0,0,0,0.35)',
            borderRadius: 8,
            padding: 4,
            gap: 4,
          }}
        >
          <button
            type="button"
            onClick={() => switchView('signup')}
            style={{
              flex: 1,
              background: view === 'signup' ? 'var(--accent-color)' : 'transparent',
              color: view === 'signup' ? '#000' : '#fff',
              padding: '8px 10px',
              fontSize: '0.9em',
            }}
          >
            {t.signUpTab}
          </button>
          <button
            type="button"
            onClick={() => switchView('signin')}
            style={{
              flex: 1,
              background: view === 'signin' ? 'var(--accent-color)' : 'transparent',
              color: view === 'signin' ? '#000' : '#fff',
              padding: '8px 10px',
              fontSize: '0.9em',
            }}
          >
            {t.signInTab}
          </button>
        </div>
      )}

      {localError && (
        <div
          role="alert"
          style={{
            background: 'rgba(248, 113, 113, 0.18)',
            border: '1px solid rgba(248, 113, 113, 0.5)',
            color: '#fca5a5',
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: '0.85em',
          }}
        >
          {localError}
        </div>
      )}

      {/* Sign Up / Sign In Form */}
      {(view === 'signup' || view === 'signin') && (
        <form onSubmit={handleAuthSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {view === 'signup' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: '0.85em' }}>{t.displayNameLabel}</label>
                <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.5)' }}>
                  {isMn ? '2–20 тэмдэгт (үсэг, тоо, _ - .)' : '2–20 chars (letters, nums, _ - .)'}
                </span>
              </div>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={20}
                required
                placeholder={t.namePlaceholder}
                style={{ width: '100%' }}
              />
            </div>
          )}

          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
              {t.emailLabel}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="player@example.com"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: '0.85em' }}>{t.passwordLabel}</label>
              {view === 'signup' && (
                <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.5)' }}>
                  {isMn ? '8+ тэмдэгт (үсэг ба тоо)' : '8+ chars (letter & number)'}
                </span>
              )}
              {view === 'signin' && (
                <button
                  type="button"
                  onClick={() => switchView('forgot')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#fde68a',
                    fontSize: '0.8em',
                    padding: 0,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {isMn ? 'Нууц үгээ мартсан?' : 'Forgot password?'}
                </button>
              )}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={view === 'signup' ? 8 : 1}
              required
              placeholder="••••••"
              style={{ width: '100%' }}
            />
          </div>

          {view === 'signup' && (
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
                {isMn ? 'Нууц үг давтах (заавал биш)' : 'Confirm Password (optional)'}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={isMn ? 'Нууц үг давтах' : 'Repeat password'}
                style={{ width: '100%' }}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={
              !email.trim() ||
              (view === 'signup' && (password.length < 8 || !displayName.trim())) ||
              (view === 'signin' && !password)
            }
            style={{ marginTop: 4 }}
          >
            {view === 'signup' ? t.signUpAction : t.signInAction}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', marginTop: 2 }}>
            <button
              type="button"
              onClick={() => switchView('verify')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#a7f3d0',
                padding: 0,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {isMn ? 'Имэйл баталгаажуулах код оруулах' : 'Verify email with code'}
            </button>
            {view === 'signup' && (
              <button
                type="button"
                onClick={() => switchView('forgot')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fde68a',
                  padding: 0,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                {isMn ? 'Нууц үг сэргээх' : 'Forgot password?'}
              </button>
            )}
          </div>
        </form>
      )}

      {/* Email Verification View */}
      {view === 'verify' && (
        <form onSubmit={handleVerifySubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              background: 'rgba(16, 185, 129, 0.14)',
              border: '1px solid rgba(16, 185, 129, 0.45)',
              borderRadius: 10,
              padding: 12,
              fontSize: '0.86em',
              lineHeight: 1.45,
            }}
          >
            <div style={{ fontWeight: 800, color: '#4ade80', marginBottom: 4 }}>
              📧 {isMn ? 'Имэйл хаяг баталгаажуулах' : 'Verify Your Email Address'}
            </div>
            <div>
              {isMn
                ? 'Таны имэйл рүү баталгаажуулах холбоос болон 6 оронтой код илгээгдлээ. Имэйл дэх холбоос дээр дарах эсвэл кодыг доор оруулна уу.'
                : 'We sent a verification link and code to your email. Click the link in your email or enter the verification code below.'}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
              {t.emailLabel}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="player@example.com"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
              {isMn ? 'Баталгаажуулах код (OTP)' : 'Verification Code (OTP)'}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              required
              maxLength={12}
              placeholder="123456"
              style={{ width: '100%', letterSpacing: 3, fontWeight: 800, textAlign: 'center' }}
            />
          </div>

          <button type="submit" disabled={!email.trim() || otpCode.trim().length < 4}>
            ✓ {isMn ? 'Имэйл баталгаажуулах' : 'Verify Email'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={!email.trim()}
              onClick={() => actions.resendVerification(email.trim())}
              style={{
                background: 'rgba(255,255,255,0.1)',
                color: '#fde68a',
                fontSize: '0.82em',
                padding: '6px 12px',
              }}
            >
              🔄 {isMn ? 'Код дахин илгээх' : 'Resend Verification Email'}
            </button>
            <button
              type="button"
              onClick={() => switchView('signin')}
              style={{
                background: 'transparent',
                color: '#fff',
                fontSize: '0.82em',
                padding: '6px 12px',
              }}
            >
              ← {isMn ? 'Нэвтрэх рүү буцах' : 'Back to Sign In'}
            </button>
          </div>
        </form>
      )}

      {/* Forgot Password View */}
      {view === 'forgot' && (
        <form onSubmit={handleForgotSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              background: 'rgba(251, 191, 36, 0.12)',
              border: '1px solid rgba(251, 191, 36, 0.4)',
              borderRadius: 10,
              padding: 12,
              fontSize: '0.86em',
              lineHeight: 1.45,
            }}
          >
            <div style={{ fontWeight: 800, color: '#fde68a', marginBottom: 4 }}>
              🔑 {isMn ? 'Нууц үг сэргээх' : 'Forgot Your Password?'}
            </div>
            <div>
              {isMn
                ? 'Бүртгэлтэй имэйл хаягаа оруулна уу. Бид таны имэйл рүү нууц үг сэргээх холбоос болон код илгээх болно.'
                : 'Enter your registered email address and we will send you a password reset link and recovery code.'}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
              {t.emailLabel}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="player@example.com"
              style={{ width: '100%' }}
            />
          </div>

          <button type="submit" disabled={!email.trim()}>
            📨 {isMn ? 'Сэргээх код илгээх' : 'Send Password Reset Email'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: '0.82em' }}>
            <button
              type="button"
              onClick={() => switchView('reset')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fde68a',
                padding: 0,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {isMn ? 'Сэргээх код аль хэдийн авсан уу?' : 'Already have a reset code?'}
            </button>
            <button
              type="button"
              onClick={() => switchView('signin')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                padding: 0,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              ← {isMn ? 'Нэвтрэх рүү буцах' : 'Back to Sign In'}
            </button>
          </div>
        </form>
      )}

      {/* Reset Password View */}
      {view === 'reset' && (
        <form onSubmit={handleResetSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              background: 'rgba(16, 185, 129, 0.14)',
              border: '1px solid rgba(16, 185, 129, 0.45)',
              borderRadius: 10,
              padding: 12,
              fontSize: '0.86em',
              lineHeight: 1.45,
            }}
          >
            <div style={{ fontWeight: 800, color: '#4ade80', marginBottom: 4 }}>
              🔐 {isMn ? 'Шинэ нууц үг тохируулах' : 'Set a New Password'}
            </div>
            <div>
              {state.recoveryToken
                ? isMn
                  ? 'Таны нууц үг сэргээх холбоос баталгаажлаа. Шинэ нууц үгээ оруулна уу.'
                  : 'Your password recovery link is verified. Enter your new password below.'
                : isMn
                ? 'Имэйлээр ирсэн сэргээх код болон шинэ нууц үгээ оруулна уу.'
                : 'Enter the recovery code sent to your email along with your new password.'}
            </div>
          </div>

          {!state.recoveryToken && (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
                  {t.emailLabel}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="player@example.com"
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
                  {isMn ? 'Сэргээх код (OTP)' : 'Recovery Code (OTP)'}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  required
                  maxLength={12}
                  placeholder="123456"
                  style={{ width: '100%', letterSpacing: 3, fontWeight: 800, textAlign: 'center' }}
                />
              </div>
            </>
          )}

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: '0.85em' }}>
                {isMn ? 'Шинэ нууц үг' : 'New Password'}
              </label>
              <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.5)' }}>
                {isMn ? '8+ тэмдэгт (үсэг ба тоо)' : '8+ chars (letter & number)'}
              </span>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              placeholder="••••••••"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em' }}>
              {isMn ? 'Шинэ нууц үг давтах' : 'Confirm New Password'}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={isMn ? 'Шинэ нууц үг давтах' : 'Repeat new password'}
              style={{ width: '100%' }}
            />
          </div>

          <button
            type="submit"
            disabled={
              password.length < 8 ||
              (!state.recoveryToken && (!email.trim() || otpCode.trim().length < 4))
            }
          >
            🔐 {isMn ? 'Нууц үг шинэчлэх' : 'Reset Password'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            {!state.recoveryToken && (
              <button
                type="button"
                disabled={!email.trim()}
                onClick={() => actions.forgotPassword(email.trim())}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fde68a',
                  fontSize: '0.82em',
                  padding: '6px 12px',
                }}
              >
                🔄 {isMn ? 'Код дахин илгээх' : 'Resend Reset Code'}
              </button>
            )}
            <button
              type="button"
              onClick={() => switchView('signin')}
              style={{
                background: 'transparent',
                color: '#fff',
                fontSize: '0.82em',
                padding: '6px 12px',
              }}
            >
              ← {isMn ? 'Нэвтрэх рүү буцах' : 'Back to Sign In'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
